/**
 * Draft-level orchestration of the client-side text-fit pass.
 *
 * Walks each `AiDraftChange.payload`, identifies slide mutations
 * (`slides.replace_content`, `resource.create` with slide kind, and
 * `slides.patch_ops` containing `insert_slide` / `replace_slide`), and runs the
 * measurement-based fit pass from [textFit.ts](../../slides/utils/textFit.ts)
 * on the embedded slide content.
 *
 * Pure function: returns a new `AiDraft` (fresh references so React detects
 * the change) and an aggregated list of warnings tagged with their source
 * `changeId`.
 */

import type { AiDraft, AiDraftChange } from '@/shared/types/chat';
import { DEFAULT_THEME } from '@/shared/theming/default-theme';
import type { ResolvedTheme } from '@/shared/types/slide';
import {
  fitSlideContent,
  fitSlideInput,
  type TextFitWarning,
} from '@/features/slides/utils/textFit';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

async function fitPatchOps(
  ops: unknown[],
  deckTheme: ResolvedTheme
): Promise<{ ops: unknown[]; warnings: TextFitWarning[] }> {
  const warnings: TextFitWarning[] = [];
  const nextOps: unknown[] = [];
  for (const op of ops) {
    if (!isRecord(op)) {
      nextOps.push(op);
      continue;
    }
    const opName = typeof op.op === 'string' ? op.op : '';
    if (opName === 'insert_slide' || opName === 'replace_slide') {
      // Fit inserted/replaced slides against the TARGET deck's theme (§9.4b), so a
      // non-default deck shrinks against its real geometry/fonts. The deck theme is
      // attached to the mutation at finalize; default when absent.
      const fitted = await fitSlideInput(op.slide, deckTheme);
      warnings.push(...fitted.warnings);
      nextOps.push({ ...op, slide: fitted.slide });
    } else {
      nextOps.push(op);
    }
  }
  return { ops: nextOps, warnings };
}

async function fitChange(
  change: AiDraftChange
): Promise<{ change: AiDraftChange; warnings: TextFitWarning[] }> {
  const payload = isRecord(change.payload) ? change.payload : null;
  if (!payload) return { change, warnings: [] };
  const kind = typeof payload.kind === 'string' ? payload.kind : '';

  if (kind === 'slides.replace_content') {
    const fitted = await fitSlideContent(payload.content);
    return {
      change: { ...change, payload: { ...payload, content: fitted.content } },
      warnings: fitted.warnings.map((w) => ({ ...w, changeId: change.changeId })),
    };
  }

  if (kind === 'resource.create' && payload.resourceKind === 'slide') {
    const fitted = await fitSlideContent(payload.content);
    return {
      change: { ...change, payload: { ...payload, content: fitted.content } },
      warnings: fitted.warnings.map((w) => ({ ...w, changeId: change.changeId })),
    };
  }

  if (kind === 'slides.patch_ops') {
    const ops = Array.isArray(payload.ops) ? payload.ops : [];
    // `deckTheme` rides on the finalized patch mutation (§9.4b). Trusted stored
    // theme shape; default for legacy decks or older payloads without it.
    const deckTheme = isRecord(payload.deckTheme)
      ? (payload.deckTheme as unknown as ResolvedTheme)
      : DEFAULT_THEME;
    const fitted = await fitPatchOps(ops, deckTheme);
    return {
      change: { ...change, payload: { ...payload, ops: fitted.ops } },
      warnings: fitted.warnings.map((w) => ({ ...w, changeId: change.changeId })),
    };
  }

  return { change, warnings: [] };
}

export async function fitDraftSlideContent(
  draft: AiDraft
): Promise<{ draft: AiDraft; warnings: TextFitWarning[] }> {
  const allWarnings: TextFitWarning[] = [];
  const nextChanges: AiDraftChange[] = [];
  for (const change of draft.changes) {
    const fitted = await fitChange(change);
    nextChanges.push(fitted.change);
    allWarnings.push(...fitted.warnings);
  }
  return {
    draft: { ...draft, changes: nextChanges },
    warnings: allWarnings,
  };
}
